import { useState } from 'react';

import { fmt, fmtDate, type LvaItemListRow, type LvaStatus } from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { Can } from '../Can';
import {
  DamageModal,
  DisposeModal,
  LostModal,
  ReassignModal,
  TransferModal,
} from './action-modals';
import { LvaAttachmentsPanel } from './LvaAttachmentsPanel';
import { LvaDetailActions } from './LvaDetailActions';
import { LvaMovementLog } from './LvaMovementLog';
import {
  useActiveCompanyId,
  useDisposeLvaItem,
  useDownloadLvaLabels,
  useLvaMovements,
  useMarkDamaged,
  useMarkLost,
  useReassignCustodian,
  useTransferLvaItem,
} from '../../lib/queries';

// 8-category palette per spec §15 — mirrors the table in lva.tsx. Kept local so
// LvaDetailView is self-contained (the scan-to-detail route renders it without
// the LvaPage component around it).
interface LvaCategory {
  code: string;
  label: string;
  tone: string;
}

const LVA_CATS: readonly LvaCategory[] = [
  { code: 'FURN', label: 'เฟอร์นิเจอร์สำนักงาน', tone: '#7030A0' },
  { code: 'ELEC', label: 'เครื่องใช้ไฟฟ้าเล็ก', tone: '#b54708' },
  { code: 'TOOL', label: 'เครื่องมือช่าง', tone: '#a16207' },
  { code: 'OFFICE', label: 'อุปกรณ์สำนักงาน', tone: '#1a56a6' },
  { code: 'CCTV', label: 'กล้องวงจรปิด / Security', tone: '#117a3d' },
  { code: 'DISPLAY', label: 'ป้าย / อุปกรณ์ Showroom', tone: '#b42318' },
  { code: 'IT_SM', label: 'IT มูลค่าต่ำ', tone: '#0e7490' },
  { code: 'OTHER', label: 'อื่นๆ', tone: '#52525b' },
];

const FALLBACK_CAT: LvaCategory = LVA_CATS[7]!;

function getCatByLabel(label: string): LvaCategory {
  return LVA_CATS.find((x) => x.label === label) ?? FALLBACK_CAT;
}

const LVA_STATUS_META: Readonly<Record<LvaStatus, { l: string; c: string }>> = {
  IN_USE: { l: 'ใช้งาน', c: 'positive' },
  DISPOSED: { l: 'จำหน่าย', c: 'neutral' },
  LOST: { l: 'สูญหาย', c: 'negative' },
};

function costNumber(c: LvaItemListRow['cost']): number {
  return typeof c === 'number' ? c : Number(c);
}

type LvaAction = 'transfer' | 'reassign' | 'damage' | 'lost' | 'dispose' | null;

export interface LvaDetailViewProps {
  item: LvaItemListRow;
  onEdit: (item: LvaItemListRow) => void;
  // Optional close affordance — the modal passes its dismiss handler so the
  // header "x" + footer "ปิด" appear; the full-page scan route omits it.
  onClose?: () => void;
}

// The body of the LVA detail (header + facts + movement log + attachments +
// quick-action footer + lifecycle sub-modals). Extracted from LvaDetailModal so
// the modal and the mobile scan-to-detail page (route lva.$code) render one
// shared body and never diverge.
export function LvaDetailView({ item, onEdit, onClose }: LvaDetailViewProps) {
  const companyId = useActiveCompanyId();
  const movementsQuery = useLvaMovements(companyId, item.id);
  const transferMut = useTransferLvaItem(companyId);
  const reassignMut = useReassignCustodian(companyId);
  const damageMut = useMarkDamaged(companyId);
  const lostMut = useMarkLost(companyId);
  const disposeMut = useDisposeLvaItem(companyId);
  // Mutation wrapper (not the raw downloadLvaLabels) so a successful single-item
  // print invalidates the lva-items cache and the printed badge flips live.
  const printLabelMut = useDownloadLvaLabels(companyId);
  const [action, setAction] = useState<LvaAction>(null);
  // Lifted error for the open action sub-modal. Server 400/404 (SoD,
  // transfer-branch-not-in-company, already-disposed, …) surface here so the
  // modal can show a red box and STAY OPEN instead of silently swallowing it.
  const [actionError, setActionError] = useState<string | null>(null);
  // Open an action modal with a clean error slate.
  const openAction = (a: LvaAction) => {
    setActionError(null);
    setAction(a);
  };
  const closeAction = () => {
    setActionError(null);
    setAction(null);
  };
  const errMsg = (e: unknown) => (e instanceof Error ? e.message : 'บันทึกล้มเหลว');
  const c = getCatByLabel(item.categoryName);
  const s = LVA_STATUS_META[item.status];

  const busy =
    transferMut.isPending ||
    reassignMut.isPending ||
    damageMut.isPending ||
    lostMut.isPending ||
    disposeMut.isPending;

  const movements = movementsQuery.data ?? [];

  return (
    <>
      <div
        style={{
          padding: '18px 22px',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div>
          <h3
            style={{
              margin: 0,
              fontSize: 18,
              fontWeight: 600,
              fontFamily: 'var(--font-display)',
            }}
          >
            {item.code} — {item.name}
          </h3>
          <div className="meta" style={{ marginTop: 2 }}>
            <span className={`badge ${s.c}`}>{s.l}</span>
          </div>
        </div>
        {onClose && (
          <button className="btn ghost" onClick={onClose} style={{ padding: '0 8px' }}>
            <Icon name="x" size={18} />
          </button>
        )}
      </div>
      <div style={{ padding: '22px', display: 'flex', gap: 18 }}>
        <div
          style={{
            width: 160,
            height: 160,
            borderRadius: 8,
            background: c.tone + '20',
            display: 'grid',
            placeItems: 'center',
            color: c.tone,
            fontSize: 32,
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          {c.code}
        </div>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8, fontSize: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span className="muted">หมวด</span>
            <b>{c.label}</b>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span className="muted">มูลค่า</span>
            <b className="tnum">{fmt(costNumber(item.cost))}</b>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span className="muted">สาขา</span>
            <b>{item.branchName ?? '-'}</b>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span className="muted">สถานที่</span>
            <b>{item.location}</b>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span className="muted">ผู้รับผิดชอบ</span>
            <b>{item.assignedTo}</b>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span className="muted">วันที่ซื้อ</span>
            <b>{fmtDate(item.acquiredDate)}</b>
          </div>
          {item.disposedDate && (
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span className="muted">วันที่จำหน่าย</span>
              <b>{fmtDate(item.disposedDate)}</b>
            </div>
          )}
        </div>
      </div>
      <div style={{ padding: '0 22px 22px' }}>
        <LvaMovementLog events={movements} />
      </div>

      <div style={{ padding: '0 22px 22px' }}>
        <LvaAttachmentsPanel companyId={companyId} lvaItemId={item.id} />
      </div>

      <div
        style={{
          padding: '14px 22px',
          borderTop: '1px solid var(--border)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 8,
          background: '#fafafa',
        }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          <Can permission="lva.update">
            <button className="btn sm" type="button" onClick={() => onEdit(item)}>
              แก้ไข
            </button>
          </Can>
          <Can permission="lva.printLabel">
            <button
              className="btn sm"
              type="button"
              disabled={!companyId || busy || printLabelMut.isPending}
              onClick={() => {
                if (!companyId) return;
                printLabelMut.mutate({
                  itemIds: [item.id],
                  layout: '8',
                  markPrinted: true,
                });
              }}
            >
              <Icon name="print" size={12} /> พิมพ์ QR
            </button>
          </Can>
          <LvaDetailActions
            item={item}
            busy={busy}
            onTransfer={() => openAction('transfer')}
            onReassign={() => openAction('reassign')}
            onMarkDamaged={() => openAction('damage')}
            onMarkLost={() => openAction('lost')}
            onDispose={() => openAction('dispose')}
          />
        </div>
        {onClose && (
          <button className="btn ghost" onClick={onClose}>
            ปิด
          </button>
        )}
      </div>

      {action === 'transfer' && (
        <TransferModal
          item={item}
          busy={transferMut.isPending}
          error={actionError}
          onClose={closeAction}
          onSubmit={(data) => {
            setActionError(null);
            transferMut.mutate(
              { id: item.id, data },
              { onSuccess: closeAction, onError: (e) => setActionError(errMsg(e)) },
            );
          }}
        />
      )}
      {action === 'reassign' && (
        <ReassignModal
          item={item}
          busy={reassignMut.isPending}
          error={actionError}
          onClose={closeAction}
          onSubmit={(data) => {
            setActionError(null);
            reassignMut.mutate(
              { id: item.id, data },
              { onSuccess: closeAction, onError: (e) => setActionError(errMsg(e)) },
            );
          }}
        />
      )}
      {action === 'damage' && (
        <DamageModal
          item={item}
          busy={damageMut.isPending}
          error={actionError}
          onClose={closeAction}
          onSubmit={(data) => {
            setActionError(null);
            damageMut.mutate(
              { id: item.id, data },
              { onSuccess: closeAction, onError: (e) => setActionError(errMsg(e)) },
            );
          }}
        />
      )}
      {action === 'lost' && (
        <LostModal
          item={item}
          busy={lostMut.isPending}
          error={actionError}
          onClose={closeAction}
          onSubmit={(data) => {
            setActionError(null);
            lostMut.mutate(
              { id: item.id, data },
              { onSuccess: closeAction, onError: (e) => setActionError(errMsg(e)) },
            );
          }}
        />
      )}
      {action === 'dispose' && (
        <DisposeModal
          item={item}
          busy={disposeMut.isPending}
          error={actionError}
          onClose={closeAction}
          onSubmit={(data) => {
            setActionError(null);
            disposeMut.mutate(
              { id: item.id, data },
              { onSuccess: closeAction, onError: (e) => setActionError(errMsg(e)) },
            );
          }}
        />
      )}
    </>
  );
}
